<template>
  <a-dropdown
    v-model:open="dropdownVisible"
    :trigger="['click']"
    placement="bottomRight"
    overlayClassName="adjust_voice_dropdown"
  >
    <a-button v-if="selectedVoice" class="secondary_medium !h-[38px]">
      <div class="flex items-center gap-2">
        <ThemeImage :icon="imageNames.TUNE" alt="adjust" class="size-4" />
        Adjust
      </div>
    </a-button>

    <template #overlay>
      <div class="w-72 adjust_voice_content bg_color border_color">
        <div class="flex items-center justify-between border_bottom p-4">
          <span class="text-sm font-medium text_color">Adjust voice</span>
          <ThemeImage
            :icon="imageNames.CROSS_GREY"
            alt="close"
            class="size-4 cursor-pointer"
            @click="handleClose"
          />
        </div>

        <div class="flex flex-col gap-2 p-2">
          <div v-if="isLoadingSettings" class="flex flex-col gap-4 p-4">
            <a-skeleton active :paragraph="{ rows: 3 }" />
          </div>

          <div
            v-else-if="settingsError"
            class="p-4 text-center text_color_secondary"
          >
            {{ settingsError }}
          </div>

          <div
            v-else-if="!hasAnySettings && voiceSettings"
            class="p-4 text-center text_color_secondary"
          >
            No adjustments available for this voice
          </div>

          <template v-else>
            <CustomSlider
              v-if="showSpeedSlider"
              :value="voiceSpeed"
              :min="0.5"
              :max="2"
              :precision="2"
              label="Speed"
              min-label="Slow"
              max-label="Fast"
              @update:value="voiceSpeed = $event"
            />

            <CustomSlider
              v-if="showVolumeSlider"
              :value="voiceVolume"
              :min="0"
              :max="2"
              :precision="2"
              label="Volume"
              min-label="Low"
              max-label="High"
              @update:value="voiceVolume = $event"
            />

            <CustomSlider
              v-if="showTemperatureSlider"
              :value="voiceTemperature"
              :min="0"
              :max="2"
              :precision="2"
              label="Temperature"
              min-label="Calm"
              max-label="Emotional"
              @update:value="voiceTemperature = $event"
            />
          </template>
        </div>

        <div class="flex justify-end gap-2 border_top p-2">
          <a-button class="secondary_medium" @click="handleClose">
            Cancel
          </a-button>
          <a-button
            class="primary_medium"
            @click="handleSave"
            :loading="isSaving"
            :disabled="isLoadingSettings || !hasAnySettings"
          >
            Save changes
          </a-button>
        </div>
      </div>
    </template>
  </a-dropdown>
</template>

<script setup>
import { ref, watch, computed } from "vue";
import CustomSlider from "@/src/components/custom_components/CustomSlider.vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import VoiceAgentService from "@/src/api/VoiceAgentService.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { message } from "ant-design-vue";

const props = defineProps({
  agentId: {
    type: [Number, String],
    default: null,
  },
  selectedVoice: {
    type: Object,
    default: null,
  },
  selectedLanguage: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["saved", "create-agent"]);

const dropdownVisible = ref(false);
const voiceSpeed = ref(1);
const voiceVolume = ref(1);
const voiceTemperature = ref(1);
const isSaving = ref(false);
const isLoadingSettings = ref(false);
const voiceSettings = ref(null);
const settingsError = ref(null);

const showSpeedSlider = computed(
  () => voiceSettings.value?.voice_speed === true
);

const showVolumeSlider = computed(
  () => voiceSettings.value?.voice_volume === true
);

const showTemperatureSlider = computed(
  () => voiceSettings.value?.voice_temperature === true
);

const hasAnySettings = computed(
  () =>
    showSpeedSlider.value ||
    showVolumeSlider.value ||
    showTemperatureSlider.value
);

const fetchVoiceModelSettings = async () => {
  if (!props.selectedVoice?.provider || !props.selectedLanguage?.voice_id) {
    settingsError.value = "Voice and language are required";
    return;
  }

  try {
    settingsError.value = null;

    const response = await VoiceAgentService.getVoiceModelSetting(
      props.selectedVoice.provider,
      props.selectedLanguage.voice_id
    );

    if (response.success) {
      voiceSettings.value = response;
    } else {
      ("Failed to load voice adjustment settings");
    }
  } catch (error) {
    console.error("Error fetching voice model settings:", error);
    settingsError.value = "Failed to load voice adjustment settings";
  }
};

const fetchSavedVoiceSettings = async () => {
  if (!props.agentId) {
    return;
  }

  try {
    const response = await VoiceAgentService.getVoiceModel(props.agentId);

    if (response?.success) {
      voiceSpeed.value = response.voice_speed ?? 1;
      voiceVolume.value = response.voice_volume ?? 1;
      voiceTemperature.value = response.voice_temperature ?? 1;
    }
  } catch (error) {
    console.error("Error fetching saved voice settings:", error);
  }
};

const handleDropdownOpen = async (isOpen) => {
  if (isOpen) {
    try {
      isLoadingSettings.value = true;
      await Promise.all([fetchVoiceModelSettings(), fetchSavedVoiceSettings()]);
      isLoadingSettings.value = false;
    } catch (error) {
      isLoadingSettings.value = false;
      console.error("Error opening dropdown:", error);
    } finally {
      isLoadingSettings.value = false;
    }
  }
};

const handleClose = () => {
  try {
    if (!isSaving.value) {
      dropdownVisible.value = false;
    }
  } catch (error) {
    console.error("Error closing dropdown:", error);
  }
};

const handleSave = async () => {
  try {
    if (!hasAnySettings.value) {
      return;
    }

    isSaving.value = true;

    let currentAgentId = props.agentId;

    if (!currentAgentId) {
      const createResult = await new Promise((resolve) => {
        emit("create-agent", resolve);
      });

      if (!createResult || !createResult.agent_id) {
        message.error("Failed to create agent");
        return;
      }

      currentAgentId = createResult.agent_id;
    }

    const payload = {
      provider: props.selectedVoice.provider,
      language: props.selectedLanguage.voice_id,
      voice_model_id: "auto",
    };

    if (showSpeedSlider.value) {
      payload.voice_speed = voiceSpeed.value;
    }
    if (showVolumeSlider.value) {
      payload.voice_volume = voiceVolume.value;
    }
    if (showTemperatureSlider.value) {
      payload.voice_temperature = voiceTemperature.value;
    }

    const response = await VoiceAgentService.updateVoiceModel(
      currentAgentId,
      payload
    );

    if (response?.success) {
      message.success("Voice settings updated successfully");
      emit("saved", {
        speed: voiceSpeed.value,
        volume: voiceVolume.value,
        temperature: voiceTemperature.value,
      });
      dropdownVisible.value = false;
    } else {
      message.error("Failed to update voice settings");
    }
  } catch (error) {
    console.error("Error saving voice settings:", error);
    message.error(
      error?.response?.data?.error ||
        error?.message ||
        "Failed to update voice settings. Please try again."
    );
  } finally {
    isSaving.value = false;
  }
};

watch(() => dropdownVisible.value, handleDropdownOpen);
</script>

<style>
.adjust_voice_dropdown .ant-dropdown-menu {
  padding: 0 !important;
  box-shadow: none !important;
  background: transparent !important;
}

.adjust_voice_content {
  border-radius: 8px;
  box-shadow: 0 4px 12px var(--black-opacity-15);
}
</style>
